import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { ArrowRightLeft, Users, FileText, Download, Loader2, AlertTriangle } from 'lucide-react';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { Modal, MultiSelect } from '../components/adminModals';
import Drawer from '../../sharedComponents/Drawer';
import {
    submissionsByDeskService,
    submissionService,
    primaFacieService,
    manuscriptReviewByDeskService,
    categoryAdminService,
    Category,
    CategoryMemberDropdownItem,
    DeskItem,
    SubmissionRevisionRequest,
    revisedCopyByDeskService,
} from '../services/adminAuth';
import { useAuth } from '../../../auth/hooks/auth';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';
import { formatRoleLabel } from '../../../utils/roleLabel';

// ─────────────────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
    Pending: { label: 'Pending', className: 'bg-yellow-50 text-yellow-700 border border-yellow-200' },
    Submitted: { label: 'Submitted', className: 'bg-blue-50 text-blue-700 border border-blue-200' },
    AcceptableForPublication: { label: 'Acceptable for Publication', className: 'bg-green-50 text-green-700 border border-green-200' },
    ReturnToAuthorForRevisedCopy: { label: 'Return to Author for Revised Copy', className: 'bg-orange-50 text-orange-700 border border-orange-200' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const cfg = STATUS_CONFIG[status] ?? { label: status, className: 'bg-gray-100 text-gray-600 border border-gray-200' };
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.className}`}>
            {cfg.label}
        </span>
    );
};

const resolveUrl = (url: string) =>
    url.startsWith('http') ? url : `${FILE_BASE_URL}/${url}`;

const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─────────────────────────────────────────────────────────────────────────────

function RevisedSubmissionsByDesk() {
    const { showNotification } = useNotification();
    const { user } = useAuth();

    const isCategoryAdmin = user?.role === 'thematicarealeader';
    const isDess = user?.role === 'dess';

    // Desk owner label for the page header (e.g. "Revised Submissions by DESS's Desk")
    const deskRoleLabel = isCategoryAdmin ? 'Thematic Area Leader'
        : isDess ? 'DESS'
        : formatRoleLabel(user?.displayRole);

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    const [categoryAdminCategoryId, setCategoryAdminCategoryId] = useState<number | null>(null);
    const [initialized, setInitialized] = useState(false);

    // ── Table state ───────────────────────────────────────────────────────────
    const [items, setItems] = useState<SubmissionRevisionRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');

    // ── View Revised Copies drawer ────────────────────────────────────────────
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerTarget, setDrawerTarget] = useState<SubmissionRevisionRequest | null>(null);
    const [drawerItems, setDrawerItems] = useState<SubmissionRevisionRequest[]>([]);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [downloadingMap, setDownloadingMap] = useState<Record<string, boolean>>({});

    // ── Move modal ────────────────────────────────────────────────────────────
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [moveTarget, setMoveTarget] = useState<SubmissionRevisionRequest | null>(null);
    const [deskList, setDeskList] = useState<DeskItem[]>([]);
    const [deskListLoading, setDeskListLoading] = useState(false);
    const [selectedDeskId, setSelectedDeskId] = useState<number | ''>('');
    const [moveComment, setMoveComment] = useState('');
    const [isMoving, setIsMoving] = useState(false);

    // ── Assign Panel modal ────────────────────────────────────────────────────
    const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
    const [assignTarget, setAssignTarget] = useState<SubmissionRevisionRequest | null>(null);
    const [categoryMembers, setCategoryMembers] = useState<CategoryMemberDropdownItem[]>([]);
    const [fetchingMembers, setFetchingMembers] = useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = useState<(string | number)[]>([]);
    const [isAssigning, setIsAssigning] = useState(false);

    // ── Communicate to Author modals ──────────────────────────────────────────
    // Shared
    const [communicateTarget, setCommunicateTarget] = useState<SubmissionRevisionRequest | null>(null);

    // ReturnToAuthorForRevisedCopy modal (reuse revised copy modal)
    const [isRevisedCopyModalOpen, setIsRevisedCopyModalOpen] = useState(false);
    const [revisedCopyComment, setRevisedCopyComment] = useState('');
    const [isRequestingRevisedCopy, setIsRequestingRevisedCopy] = useState(false);

    // AcceptableForPublication modal
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [approveComment, setApproveComment] = useState('');
    const [isApproving, setIsApproving] = useState(false);

    // ── Bootstrap Category Admin ──────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            if (!isCategoryAdmin) {
                setInitialized(true);
                return;
            }
            try {
                const res = await categoryAdminService.getCategoriesForCategoryAdmin();
                if (res.isSuccess && res.data && res.data.length > 0) {
                    setCategoryAdminCategoryId(res.data[0].id);
                } else {
                    setCategoryAdminCategoryId(0);
                }
            } catch {
                setCategoryAdminCategoryId(0);
            } finally {
                setInitialized(true);
            }
        };
        init();
    }, [isCategoryAdmin]);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!initialized) return;
        if (isCategoryAdmin && categoryAdminCategoryId === null) return;

        const handler = setTimeout(() => { fetchItems(); }, 400);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, search, isCategoryAdmin, categoryAdminCategoryId]);

    const fetchItems = async () => {
        if (isCategoryAdmin && (categoryAdminCategoryId === null || categoryAdminCategoryId === 0)) {
            setLoading(false);
            setItems([]);
            return;
        }
        setLoading(true);
        try {
            const response = await revisedCopyByDeskService.getSubmissionRevisionByDesk({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                search,
                categoryId: isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : 0,
            });
            if (response.isSuccess && response.data) {
                setItems(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch revised submissions' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching revised submissions' });
        } finally {
            setLoading(false);
        }
    };

    // ── View Revised Copies drawer ────────────────────────────────────────────
    const openDrawer = async (row: SubmissionRevisionRequest) => {
        setDrawerTarget(row);
        setDrawerItems([]);
        setIsDrawerOpen(true);
        setDrawerLoading(true);
        try {
            const res = await revisedCopyByDeskService.getRevisionRequestsBySubmissionId(row.submissionId);
            if (res.isSuccess && res.data) {
                setDrawerItems(res.data);
            } else {
                showNotification('error', { title: 'Error', message: res.message || 'Failed to load revision history' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setDrawerLoading(false);
        }
    };

    const handleDownload = async (url: string, key: string, filename: string) => {
        setDownloadingMap(prev => ({ ...prev, [key]: true }));
        try {
            await downloadFileFromUrl(resolveUrl(url), filename);
        } finally {
            setDownloadingMap(prev => ({ ...prev, [key]: false }));
        }
    };

    // ── Move handlers ─────────────────────────────────────────────────────────
    const openMoveModal = async (row: SubmissionRevisionRequest) => {
        setMoveTarget(row);
        setSelectedDeskId('');
        setMoveComment('');
        setIsMoveModalOpen(true);
        setDeskListLoading(true);
        try {
            const res = await submissionsByDeskService.getDeskList();
            setDeskList(res.isSuccess && res.data ? res.data : []);
        } catch {
            showNotification('error', { title: 'Error', message: 'Failed to load desk list' });
        } finally {
            setDeskListLoading(false);
        }
    };

    const closeMoveModal = () => {
        setIsMoveModalOpen(false);
        setMoveTarget(null);
        setSelectedDeskId('');
        setMoveComment('');
    };

    const handleMove = async () => {
        if (!moveTarget || selectedDeskId === '') return;
        setIsMoving(true);
        try {
            const res = await submissionsByDeskService.moveSubmission({
                submissionId: moveTarget.submissionId,
                comment: moveComment,
                toWorkflowStage: selectedDeskId as number,
            });
            if (res.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Submission moved successfully' });
                closeMoveModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Error', message: res.message || 'Failed to move submission' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred while moving' });
        } finally {
            setIsMoving(false);
        }
    };

    // ── Assign Panel handlers ─────────────────────────────────────────────────
    const openAssignPanelModal = async (row: SubmissionRevisionRequest) => {
        setAssignTarget(row);
        setSelectedMemberIds([]);
        setIsAssignPanelOpen(true);
        setFetchingMembers(true);
        try {
            // Use categoryAdminCategoryId since only category admins see this button
            const res = await primaFacieService.getCategoryMemberDropdown(categoryAdminCategoryId ?? 0);
            if (res.isSuccess && res.data) {
                setCategoryMembers(res.data);
            } else {
                showNotification('error', { title: 'Error', message: res.message || 'Failed to load members' });
                setCategoryMembers([]);
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while loading members' });
            setCategoryMembers([]);
        } finally {
            setFetchingMembers(false);
        }
    };

    const closeAssignPanelModal = () => {
        setIsAssignPanelOpen(false);
        setAssignTarget(null);
        setSelectedMemberIds([]);
        setCategoryMembers([]);
    };

    const handleAssignPanel = async () => {
        if (!assignTarget || selectedMemberIds.length === 0) return;
        setIsAssigning(true);
        try {
            const assignedMembers = selectedMemberIds.map(id => {
                const member = categoryMembers.find(m => m.id === id);
                return { memberId: id.toString(), name: member?.name ?? '' };
            });
            const res = await revisedCopyByDeskService.assignMembersForRevisedCopyReview({
                submissionRevisionRequestId: assignTarget.submissionRevisionRequestId,
                assignedMembers,
            });
            if (res.isSuccess) {
                const failed = (res.data ?? []).filter(r => !r.successful);
                if (failed.length > 0) {
                    showNotification('error', {
                        title: 'Partial Assignment',
                        message: `${failed.length} member(s) could not be assigned: ${failed.map(f => f.reason).join(', ')}`,
                    });
                } else {
                    showNotification('success', { title: 'Success', message: 'Panel assigned successfully' });
                }
                closeAssignPanelModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Assignment Failed', message: res.message || 'Failed to assign panel' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred during assignment' });
        } finally {
            setIsAssigning(false);
        }
    };

    // ── Communicate to Author ─────────────────────────────────────────────────
    const openCommunicateModal = (row: SubmissionRevisionRequest) => {
        setCommunicateTarget(row);
        if (row.status === 'ReturnToAuthorForRevisedCopy') {
            setRevisedCopyComment('');
            setIsRevisedCopyModalOpen(true);
        } else if (row.status === 'AcceptableForPublication') {
            setApproveComment('');
            setIsApproveModalOpen(true);
        }
    };

    // ReturnToAuthorForRevisedCopy — reuse requestRevisedCopyFromAuthor
    const closeRevisedCopyModal = () => {
        if (isRequestingRevisedCopy) return;
        setIsRevisedCopyModalOpen(false);
        setCommunicateTarget(null);
        setRevisedCopyComment('');
    };

    const handleRequestRevisedCopy = async () => {
        if (!communicateTarget || !revisedCopyComment.trim()) return;
        setIsRequestingRevisedCopy(true);
        try {
            const res = await manuscriptReviewByDeskService.requestRevisedCopyFromAuthor({
                submissionId: communicateTarget.submissionId,
                comment: revisedCopyComment.trim(),
            });
            if (res.isSuccess) {
                showNotification('success', { title: 'Success', message: res.message || 'Revised copy requested successfully' });
                closeRevisedCopyModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Failed', message: res.message || 'Could not request revised copy' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setIsRequestingRevisedCopy(false);
        }
    };

    // AcceptableForPublication — communicate approved status to author
    const closeApproveModal = () => {
        if (isApproving) return;
        setIsApproveModalOpen(false);
        setCommunicateTarget(null);
        setApproveComment('');
    };

    const handleApprove = async () => {
        if (!communicateTarget) return;
        setIsApproving(true);
        try {
            const res = await submissionService.communicateSubmissionStatusToAuthor({
                submissionId: communicateTarget.submissionId,
                comment: approveComment.trim(),
                submissionStatus: 5, // Approved
            });
            if (res.isSuccess) {
                showNotification('success', { title: 'Success', message: res.message || 'Author notified successfully' });
                closeApproveModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Failed', message: res.message || 'Could not communicate status' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setIsApproving(false);
        }
    };

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns: TableColumn<SubmissionRevisionRequest>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (_, row) => (
                <span className="text-gray-500 text-sm font-mono">{row.submissionNumber}</span>
            ),
        },
        {
            key: 'submissionTitle',
            label: 'Title / Authors',
            render: (_, row) => {
                const authors = row.bookAuthors.length > 0
                    ? row.bookAuthors.map(a => a.name).join(', ')
                    : '—';
                return (
                    <div>
                        <div className="font-medium text-gray-900 mb-0.5">{row.submissionTitle}</div>
                        <div className="text-xs text-gray-500">{authors}</div>
                    </div>
                );
            },
        },
        {
            key: 'requestedAt',
            label: 'Request Date',
            render: (_, row) => (
                <span className="text-sm text-gray-700">{formatDate(row.requestedAt)}</span>
            ),
        },
        {
            key: 'submittedAt',
            label: 'Date Submitted',
            render: (_, row) => (
                <span className="text-sm text-gray-700">{formatDate(row.submittedAt)}</span>
            ),
        },
        {
            key: 'status',
            label: 'Status',
            render: (_, row) => <StatusBadge status={row.status} />,
        },
    ];

    // ── Actions ───────────────────────────────────────────────────────────────
    const actions: TableAction<SubmissionRevisionRequest>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Revised Copies',
                    onClick: openDrawer,
                    className: 'text-blue-600',
                },
                {
                    label: 'Move Submission',
                    onClick: openMoveModal,
                    className: () => {
                        if (isCategoryAdmin || isDess) return 'hidden';
                        return 'text-blue-600';
                    },
                },
                {
                    label: 'Assign Panel for Revision Assessment',
                    onClick: openAssignPanelModal,
                    className: isCategoryAdmin ? 'text-blue-600' : 'hidden',
                },
                {
                    label: 'Communicate to Author',
                    onClick: openCommunicateModal,
                    className: (row) => {
                        if (!isDess) return 'hidden';
                        if (row.status === 'AcceptableForPublication' || row.status === 'ReturnToAuthorForRevisedCopy') {
                            return 'text-[#088A61] font-semibold';
                        }
                        return 'hidden';
                    },
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="w-full">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    {deskRoleLabel ? `Revised Submissions by ${deskRoleLabel}'s Desk` : 'Revised Submissions by Desk'}
                </h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    View and manage revised manuscript submissions currently at your desk.
                </p>
            </div>

            {isCategoryAdmin && categoryAdminCategoryId === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center mb-6">
                    <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">No Thematic Area Assigned</h3>
                    <p className="text-gray-600">You haven't been assigned to any thematic area yet. Please contact an administrator.</p>
                </div>
            )}

            <ReusableTable
                title="Revised Submissions"
                subTitle="Revised manuscript submissions at your desk"
                columns={columns}
                data={items}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title or submission number..."
                onSearch={(q) => { setSearch(q); setCurrentPage(1); }}
                emptyMessage="No revised submissions found at your desk."
                showSearch={true}
                showFilter={false}
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            {/* ── View Revised Copies Drawer ─────────────────────────────────── */}
            <Drawer
                isOpen={isDrawerOpen}
                onClose={() => { setIsDrawerOpen(false); setDrawerTarget(null); setDrawerItems([]); }}
                title="Revision History"
                width="max-w-2xl"
            >
                {drawerLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="w-6 h-6 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
                    </div>
                ) : drawerItems.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-12">No revision records found.</p>
                ) : (
                    <div className="space-y-5 pb-10">
                        <p className="text-sm text-gray-500">
                            {drawerItems.length} revision request{drawerItems.length !== 1 ? 's' : ''} for this submission
                        </p>
                        {drawerItems.map((item, idx) => (
                            <div key={item.submissionRevisionRequestId} className="border border-gray-100 rounded-xl p-5 bg-white shadow-sm space-y-4">
                                {/* Header */}
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">{item.submissionTitle}</p>
                                        <p className="text-xs text-gray-400 font-mono mt-0.5">{item.submissionNumber}</p>
                                    </div>
                                    <StatusBadge status={item.status} />
                                </div>

                                {/* Authors */}
                                {item.bookAuthors.length > 0 && (
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Authors</p>
                                        <p className="text-sm text-gray-700">{item.bookAuthors.map(a => a.name).join(', ')}</p>
                                    </div>
                                )}

                                {/* Dates */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Requested</p>
                                        <p className="text-sm text-gray-700">{formatDate(item.requestedAt)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Submitted</p>
                                        <p className="text-sm text-gray-700">{formatDate(item.submittedAt)}</p>
                                    </div>
                                </div>

                                {/* Documents */}
                                <div className="space-y-2">
                                    {item.reviewedManuscriptUrl && (
                                        <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                <span className="text-xs font-medium text-gray-700">Reviewed Manuscript</span>
                                            </div>
                                            <button
                                                disabled={!!downloadingMap[`reviewed-${idx}`]}
                                                onClick={() => handleDownload(
                                                    item.reviewedManuscriptUrl,
                                                    `reviewed-${idx}`,
                                                    `${item.submissionNumber}_reviewed.docx`
                                                )}
                                                className="flex items-center gap-1 text-xs px-2.5 py-1 bg-white border border-blue-200 rounded-lg text-blue-700 hover:bg-blue-50 disabled:opacity-60 transition-colors"
                                            >
                                                {downloadingMap[`reviewed-${idx}`]
                                                    ? <><Loader2 className="w-3 h-3 animate-spin" />Downloading...</>
                                                    : <><Download className="w-3 h-3" />Download</>
                                                }
                                            </button>
                                        </div>
                                    )}
                                    {item.revisedVersionUrl && (
                                        <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-lg px-3 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                <span className="text-xs font-medium text-gray-700">Revised Version (Author)</span>
                                            </div>
                                            <button
                                                disabled={!!downloadingMap[`revised-${idx}`]}
                                                onClick={() => handleDownload(
                                                    item.revisedVersionUrl!,
                                                    `revised-${idx}`,
                                                    `${item.submissionNumber}_revised_v${idx + 1}.docx`
                                                )}
                                                className="flex items-center gap-1 text-xs px-2.5 py-1 bg-white border border-green-200 rounded-lg text-green-700 hover:bg-green-50 disabled:opacity-60 transition-colors"
                                            >
                                                {downloadingMap[`revised-${idx}`]
                                                    ? <><Loader2 className="w-3 h-3 animate-spin" />Downloading...</>
                                                    : <><Download className="w-3 h-3" />Download</>
                                                }
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Comment */}
                                {item.comment && (
                                    <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Admin Comment</p>
                                        <p className="text-sm text-gray-700">{item.comment}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>

            {/* ── Move Modal ─────────────────────────────────────────────────── */}
            <Modal
                isOpen={isMoveModalOpen}
                onClose={closeMoveModal}
                title="Move Submission"
                description="Move this submission to another desk."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {moveTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{moveTarget.submissionTitle}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{moveTarget.submissionNumber}</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Move To <span className="text-red-500">*</span>
                        </label>
                        {deskListLoading ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading desks...
                            </div>
                        ) : (
                            <select
                                value={selectedDeskId}
                                onChange={(e) => setSelectedDeskId(e.target.value === '' ? '' : Number(e.target.value))}
                                className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                            >
                                <option value="">Select destination desk...</option>
                                {deskList.map((desk) => (
                                    <option key={desk.id} value={desk.id}>{desk.name}</option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={moveComment}
                            onChange={(e) => setMoveComment(e.target.value)}
                            placeholder="Add a note about this move..."
                            rows={3}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeMoveModal} disabled={isMoving}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleMove} disabled={isMoving || selectedDeskId === ''}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isMoving ? (
                                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Moving...</>
                            ) : (
                                <><ArrowRightLeft className="w-4 h-4" />Move Submission</>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Assign Panel Modal ─────────────────────────────────────────── */}
            <Modal
                isOpen={isAssignPanelOpen}
                onClose={closeAssignPanelModal}
                title="Assign Panel for Revision Assessment"
                description="Select thematic area members to assess this revised submission."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {assignTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{assignTarget.submissionTitle}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{assignTarget.submissionNumber}</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Panel Members <span className="text-red-500">*</span>
                            <span className="text-gray-400 font-normal ml-1">(select 1 or more)</span>
                        </label>
                        {fetchingMembers ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading members...
                            </div>
                        ) : categoryMembers.length === 0 ? (
                            <div className="w-full px-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No members available for this thematic area
                            </div>
                        ) : (
                            <MultiSelect
                                options={categoryMembers.map(m => ({ value: m.id, label: m.name }))}
                                selectedValues={selectedMemberIds}
                                onChange={setSelectedMemberIds}
                                placeholder="Select panel members..."
                            />
                        )}
                        {selectedMemberIds.length > 0 && (
                            <p className="text-xs mt-1.5 font-medium text-green-700">
                                {selectedMemberIds.length} selected
                            </p>
                        )}
                    </div>

                    {selectedMemberIds.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 space-y-1.5">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected Panel</p>
                            {selectedMemberIds.map(id => {
                                const member = categoryMembers.find(m => m.id === id);
                                return member ? (
                                    <div key={String(id)} className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs flex-shrink-0">
                                            {member.name.charAt(0)}
                                        </div>
                                        <span className="text-sm text-gray-700">{member.name}</span>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeAssignPanelModal} disabled={isAssigning}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleAssignPanel} disabled={isAssigning || selectedMemberIds.length === 0}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isAssigning ? (
                                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Assigning...</>
                            ) : (
                                <><Users className="w-4 h-4" />Assign Panel</>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Request Revised Copy Modal (ReturnToAuthorForRevisedCopy) ─── */}
            <Modal
                isOpen={isRevisedCopyModalOpen}
                onClose={closeRevisedCopyModal}
                title="Request Revised Copy From Author"
                description="Send a request to the author to submit a revised copy of this manuscript."
            >
                <div className="space-y-4">
                    {communicateTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{communicateTarget.submissionTitle}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{communicateTarget.submissionNumber}</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-yellow-50 text-yellow-700 border border-yellow-100 mt-1">
                                Return to Author for Revised Copy
                            </span>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Comment <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={revisedCopyComment}
                            onChange={(e) => setRevisedCopyComment(e.target.value)}
                            placeholder="Provide instructions or reasons for requesting a revised copy…"
                            rows={4}
                            className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                        <button onClick={closeRevisedCopyModal} disabled={isRequestingRevisedCopy}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleRequestRevisedCopy} disabled={isRequestingRevisedCopy || !revisedCopyComment.trim()}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {isRequestingRevisedCopy ? 'Submitting…' : 'Request Revised Copy'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Approve Modal (AcceptableForPublication) ───────────────────── */}
            <Modal
                isOpen={isApproveModalOpen}
                onClose={closeApproveModal}
                title="Communicate Approval to Author"
                description="Notify the author that their revised submission is acceptable for publication."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {communicateTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{communicateTarget.submissionTitle}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{communicateTarget.submissionNumber}</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-green-50 text-green-700 border border-green-100 mt-1">
                                Acceptable for Publication → Approved
                            </span>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Status
                        </label>
                        <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-semibold text-green-700">
                            Approved
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={approveComment}
                            onChange={(e) => setApproveComment(e.target.value)}
                            placeholder="Add a message to the author..."
                            rows={4}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeApproveModal} disabled={isApproving}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleApprove} disabled={isApproving}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isApproving ? (
                                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</>
                            ) : (
                                'Communicate to Author'
                            )}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default RevisedSubmissionsByDesk;